import { handleAjaxError } from "../actions/handleAjaxError";
import { showSessionArchivedState } from "./archiveModal";
import { ACCESS_REQUIREMENT_CODES } from "../helpers/accessRequirementErrors";
import { truncateSessionPreview } from "../helpers/sessionPreviewText";

export function chatSessions() {
    // Wait for shell hydration on cached-shell pages (same pattern as chatContent).
    if (window._growtypeChatShellPending) {
        document.addEventListener('growtype_chat_shell_ready', function onShellReady() {
            document.removeEventListener('growtype_chat_shell_ready', onShellReady);
            // The session list now has its own lean REST bootstrap. Start it as
            // soon as the active conversation is ready instead of adding an
            // artificial five-second wait to every chat visit.
            _chatSessionsInit();
        });
        return;
    }
    _chatSessionsInit();
}

function _chatSessionsInit() {
    /**
     * Sessions content render
     */
    let sessionsLoading = false;
    let apiNextCursor = '';
    let apiHasMore = true;
    let legacyHasMore = true;
    let chatSessionsContentRender = $('.g-chatsessions-content-render');
    const container = document.querySelector('.g-chatsessions-sessions');

    if (chatSessionsContentRender.length > 0) {
        renderSessions();

        container.addEventListener('scroll', () => {
            const scrollBottom = container.scrollTop + container.clientHeight;
            const scrollHeight = container.scrollHeight;

            if (scrollBottom >= scrollHeight) {
                if (!sessionsLoading && (usesApiV2() ? apiHasMore : legacyHasMore)) {
                    sessionsLoading = true;
                    let offset = $(container).find('.g-chatsessions-single').length;
                    $(container).append("<div class='spinner-wrapper'><span class='spinner-border'></span></div>")
                    renderSessions(offset);
                }
            }
        });
    }

    function renderSessions(offset = 0) {
        sessionsLoading = true;
        if (usesApiV2()) {
            renderApiV2Sessions(offset);
            return;
        }
        renderLegacySessions(offset);
    }

    function usesApiV2() {
        return Boolean(growtype_chat_ajax.is_logged_in && window.talkiemateApiV2Client?.listChatSessions);
    }

    function renderApiV2Sessions(offset = 0) {
        const cursor = offset === 0 ? '' : apiNextCursor;
        window.talkiemateApiV2Client.listChatSessions(cursor).then((payload) => {
            const sessions = Array.isArray(payload?.data) ? payload.data : [];
            const fragment = document.createDocumentFragment();
            sessions.filter((session) => !session?.archived).forEach((session) => {
                const card = createApiSessionCard(session);
                if (card) fragment.appendChild(card);
            });

            if (offset === 0 && chatSessionsContentRender.length > 0) {
                chatSessionsContentRender[0].replaceWith(fragment);
                chatSessionsContentRender = $();
            } else {
                container.appendChild(fragment);
            }

            apiNextCursor = typeof payload?.meta?.next_cursor === 'string'
                ? payload.meta.next_cursor
                : '';
            apiHasMore = Boolean(payload?.meta?.has_more && apiNextCursor);
            finishRender();
        }).catch(() => {
            // During rollout, an unavailable projection must fall back to the
            // established WordPress renderer instead of breaking conversations.
            if (offset === 0) {
                renderLegacySessions(0);
                return;
            }
            apiHasMore = false;
            finishRender();
        });
    }

    function createApiSessionCard(session) {
        const token = typeof session?.token === 'string' ? session.token : '';
        if (!/^[A-Za-z0-9_-]{8,191}$/.test(token)) return null;
        const summary = session.roleplay || session.character || {};
        const title = String(summary.title || session.roleplay_slug || session.character_slug || '').trim();
        if (!title) return null;

        const card = document.createElement('a');
        const path = typeof session?.links?.web === 'string' && /^\/chat\/[A-Za-z0-9_-]{8,191}$/.test(session.links.web)
            ? session.links.web
            : `/chat/${encodeURIComponent(token)}`;
        card.href = new URL(path, window.location.origin).toString();
        card.className = 'g-chatsessions-single';
        card.dataset.token = token;
        if (getActiveShellSessionToken() === token) {
            card.classList.add('is-active', 'active');
        }

        const imageUrl = typeof summary.image === 'string' ? summary.image.trim() : '';
        if (imageUrl) {
            const imageWrapper = document.createElement('div');
            imageWrapper.className = 'g-chatsessions-single-image';
            const imageInner = document.createElement('div');
            imageInner.className = 'g-chatsessions-single-image-user';
            imageInner.style.position = 'relative';
            imageInner.style.overflow = 'hidden';
            const media = createPreviewMedia(imageUrl);
            if (media) imageInner.appendChild(media);
            imageWrapper.appendChild(imageInner);
            if (session.unread) {
                const unread = document.createElement('span');
                unread.className = 'unread-bubble anim-pulse';
                imageWrapper.appendChild(unread);
            }
            card.appendChild(imageWrapper);
        }

        const content = document.createElement('div');
        content.className = 'g-chatsessions-single-content';
        const titleElement = document.createElement('p');
        titleElement.className = 'g-chatsessions-single-content-title';
        titleElement.textContent = title;
        const description = document.createElement('p');
        description.className = 'g-chatsessions-single-content-desc';
        description.textContent = truncateSessionPreview(session?.last_message?.text);
        content.append(titleElement, description);
        card.appendChild(content);

        const actions = document.createElement('div');
        actions.className = 'g-chatsessions-single-actions';
        const close = document.createElement('i');
        close.className = 'icon-close fa-solid fa-circle-xmark';
        const arrow = document.createElement('i');
        arrow.className = 'icon-arrow fa-solid fa-arrow-right';
        actions.append(close, arrow);
        card.appendChild(actions);

        const time = document.createElement('div');
        time.className = 'g-chatsessions-single-time';
        time.textContent = formatSessionTime(session?.last_message?.updated_at || session?.updated_at);
        card.appendChild(time);
        return card;
    }

    function createPreviewMedia(url) {
        let parsed;
        try {
            parsed = new URL(url, window.location.origin);
        } catch (error) {
            return null;
        }
        if (parsed.protocol !== 'https:'
            && !(parsed.protocol === 'http:' && parsed.origin === window.location.origin)) return null;
        const isVideo = /\.(mp4|webm|mov|ogg)$/i.test(parsed.pathname);
        const media = document.createElement(isVideo ? 'video' : 'img');
        media.src = parsed.toString();
        media.style.position = 'absolute';
        media.style.inset = '0';
        media.style.width = '100%';
        media.style.height = '100%';
        media.style.objectFit = 'cover';
        media.style.pointerEvents = 'none';
        if (isVideo) {
            media.autoplay = true;
            media.loop = true;
            media.muted = true;
            media.playsInline = true;
            media.preload = 'metadata';
        } else {
            media.alt = '';
            media.loading = 'lazy';
            media.decoding = 'async';
        }
        return media;
    }

    function formatSessionTime(value) {
        if (typeof value !== 'string' || !value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        const now = new Date();
        const sameDay = date.getFullYear() === now.getFullYear()
            && date.getMonth() === now.getMonth()
            && date.getDate() === now.getDate();
        return new Intl.DateTimeFormat(undefined, sameDay
            ? { hour: 'numeric', minute: '2-digit' }
            : { weekday: 'short' }
        ).format(date);
    }

    function finishRender() {
        $(container).find('.spinner-wrapper').remove();
        sessionsLoading = false;
        bindSessionActions($(container).find('.g-chatsessions-single'));
        document.dispatchEvent(new CustomEvent('growtypeChatSessionsRendered', {
            detail: { container: container }
        }));
    }

    function renderLegacySessions(offset = 0) {
        let rawArgs = chatSessionsContentRender.attr('data-args') || '{}';
        let parsedArgs = {};
        try {
            parsedArgs = JSON.parse(rawArgs);
        } catch (e) {}

        const shellToken = getActiveShellSessionToken();
        const currentPath = window.location.pathname;
        if (shellToken) {
            parsedArgs.current_session_token = shellToken;
        } else if (currentPath.includes('/chat/')) {
            const currentSlug = currentPath.split('/').filter(Boolean).pop();
            if (currentSlug && currentSlug !== 'chat') {
                parsedArgs.current_session_token = currentSlug;
            }
        }

        // WordPress REST cookie authentication requires its own wp_rest nonce.
        // The chat nonce is user-scoped, so a logged-in REST fallback is reset
        // to the anonymous user before wp_verify_nonce() and always fails. Keep
        // authenticated legacy fallback on admin-ajax, which preserves the
        // established logged-in cookie context. Anonymous requests can use the
        // lean sessions REST endpoint safely.
        const canUseSessionsRest = !growtype_chat_ajax.is_logged_in;
        let sessionsRestUrl = canUseSessionsRest
            ? (growtype_chat_ajax.sessions_rest_url || '')
            : '';
        if (canUseSessionsRest && !sessionsRestUrl) {
            try {
                sessionsRestUrl = new URL(
                    '/wp-json/growtype-chat/v1/sessions',
                    growtype_chat_ajax.url || window.location.origin
                ).toString();
            } catch (e) {}
        }
        const requestOptions = {
            url: sessionsRestUrl || growtype_chat_ajax.url,
            type: 'post',
            contentType: sessionsRestUrl ? 'application/json; charset=UTF-8' : undefined,
            processData: !sessionsRestUrl,
            success: function (response) {
                let newSessions = $(response.data.render);

                if ($('.g-chatsessions-content-render').length > 0) {
                    chatSessionsContentRender.replaceWith(newSessions)
                } else {
                    $('.g-chatsessions .g-chatsessions-sessions').append(newSessions);
                }

                $(container).find('.spinner-wrapper').remove();

                if (newSessions.length > 0) {
                    sessionsLoading = false;
                } else {
                    legacyHasMore = false;
                }

                bindSessionActions(newSessions);

                document.dispatchEvent(new CustomEvent('growtypeChatSessionsRendered', {
                    detail: { container: container }
                }));
            },
            error: function (response) {
                $(container).find('.spinner-wrapper').remove();
                sessionsLoading = false;
                handleAjaxError(response);
            }
        };

        // Build the payload immediately before sending. Cached/snapshot pages
        // hydrate growtype_chat_ajax.nonce asynchronously, so serializing the
        // body before the barrier resolves would preserve a stale placeholder
        // even though the global nonce has already been refreshed.
        const sendRequest = () => {
            const data = sessionsRestUrl ? JSON.stringify({
                args: parsedArgs,
                offset: offset,
                nonce: growtype_chat_ajax.nonce,
            }) : {
                action: 'chat_sessions_content_render',
                args: JSON.stringify(parsedArgs),
                offset: offset,
                nonce: growtype_chat_ajax.nonce,
            };
            jQuery.ajax({ ...requestOptions, data });
        };

        const prefetched = offset === 0
            ? window._growtypeChatShellInitialResponses?.sessions
            : null;
        if (prefetched) {
            delete window._growtypeChatShellInitialResponses.sessions;
            requestOptions.success(prefetched);
            return;
        }

        const snapshotBarrier = window.talkiemateSnapshotViewer;
        if (snapshotBarrier && typeof snapshotBarrier.then === 'function') {
            snapshotBarrier.then(() => {
                sendRequest();
            }).catch(() => {
                // A shared snapshot must never submit placeholder credentials.
                // Let WordPress render a fresh private document when viewer
                // bootstrap is unavailable instead of surfacing a false
                // "Security check failed" modal.
                const fallback = new URL(window.location.href);
                fallback.searchParams.set('edge_fallback', '1');
                window.location.replace(fallback.toString());
            });
            return;
        }

        sendRequest();
    }

    function bindSessionActions(sessions) {
        $(sessions).find('.icon-close').off('click.talkiemateSessions').on('click.talkiemateSessions', function (event) {
                    event.preventDefault();

                    if (!growtype_chat_ajax.is_logged_in) {
                        handleAjaxError({
                            responseJSON: {
                                data: {
                                    message: ACCESS_REQUIREMENT_CODES.PROFILE_REQUIRED
                                }
                            }
                        });
                        return false;
                    }

                    let sessionContainer = $(this).closest('.g-chatsessions-single');
                    let isActive = sessionContainer.hasClass('active') || sessionContainer.hasClass('is-active');
                    let sessionToken = sessionContainer.attr('data-token');

                    jQuery.ajax({
                        url: growtype_chat_ajax.url,
                        type: 'post',
                        data: {
                            action: growtype_chat_ajax.session_delete_action,
                            session: {
                                token: sessionToken
                            },
                            nonce: growtype_chat_ajax.nonce,
                        }, success: function (response) {
                            if (!response.success) {
                                handleAjaxError(response);
                                return;
                            }

                            let currentLastPart = getLastPathSegment(window.location.href);
                            showSessionArchivedState(sessionContainer, response.data.message).then(function () {
                                if (isActive || currentLastPart === sessionToken) {
                                    window.location.replace(response.data.redirect_url);
                                    return;
                                }

                                sessionContainer.remove();
                            });
                        },
                        error: function (response) {
                            handleAjaxError(response);
                        }
                    });

                    return false;
                });
    }

    function getLastPathSegment(url) {
        let path = new URL(url, window.location.origin).pathname; // Ensure we always work with an absolute URL
        let segments = path.split('/').filter(segment => segment !== '');
        return segments[segments.length - 1];
    }

    function getActiveShellSessionToken() {
        const shell = document.querySelector('#gc-shell-chat');
        if (!shell) {
            return '';
        }

        const token = shell.getAttribute('data-session-token') || '';

        if (!token || token.includes('__SHELL_')) {
            return '';
        }

        return token;
    }

    function removeLastPathSegment(url) {
        let path = new URL(url, window.location.origin).pathname; // Get the path of the URL
        let segments = path.split('/').filter(segment => segment !== ''); // Split into segments, removing empty ones

        segments.pop();

        return window.location.origin + '/' + segments.join('/');
    }
}
